import {LitElement, css, html} from 'lit';
import {customElement, property, state} from 'lit/decorators.js';
import {Task} from "@lit/task";
import {ContextProvider} from '@lit/context';
import {artistContext} from './context/artist-context.js';
import {changeUrlParameter} from "./services/artistService.ts";
import {getData} from "./services/cacheService.ts";
// import {setLocaleFromDocumentLang} from './localization.js';

// const STORAGE_KEY = 'pvArtistData';
// const CACHE_DURATION = 1000 * 60 * 60 ; // 1 hour

@customElement('artist-wrapper')
export class ArtistWrapper extends LitElement {

  /**
   * Initial Path for API
   */
  @property({attribute: 'initial-search-path'})
  initialSearchPath?: string;

  @property({reflect: true})
  view?: string = 'grid';

  /**
   * Context
   */
  private _provider = new ContextProvider(this, {
    context: artistContext,
    initialValue: {
      loading: false,
      data: null,
      view: 'grid'
    }
  });

  @state()
  private _apiLink?:string;

  @state()
  private _items?:any = [];

  private _getDataTask = new Task(this, {
    task: async ([],{}) => {

      const url:string = this._apiLink ? decodeURIComponent(this._apiLink) : ''
      this._provider.setValue({loading: true});
      try {
        const data = await getData(url);

        if (data) {
          // todo: should be cleaner and not
          if (data?.searchRequest?.page > 1) {
            this._items = this._items.concat(data.items)
          } else {
            this._items = data.items
          }
          data.items = this._items

          this._provider.setValue({data: data})
          this._scrollToFilterList()
        }

      } catch (error) {
        console.error({ error });
      } finally {
        this._provider.setValue({loading: false});
      }
    },
    args: () => [],
  });
  createRenderRoot() {
    return this;
  }
  connectedCallback() {
    super.connectedCallback()
    this.addEventListener('artistSearchChange', this._handleSearchChange);
    this.addEventListener('artistFilterChange', this._handleFilterChange);
    this.addEventListener('artistLoadMore', this._handleLoadMoreChange);
    this.addEventListener('artistViewChange', this._handleViewChange);

    this._apiLink = this.initialSearchPath
    const apiLinkIdentifier = this._getApiLinkIdentifier();
    let sessionApiLink = sessionStorage.getItem(apiLinkIdentifier)
    if (sessionApiLink) {
      this._apiLink = sessionApiLink
    }

    window.addEventListener("hashchange", () => {
      this._reloadIfHashExistsAndFiltered()
    });
  }

  private _handleSearchChange(e: any) {
    let newSearchTerm = e.detail
    let url = this._apiLink ? this._apiLink : ""
    this._apiLink = changeUrlParameter(url, [{term: newSearchTerm}])
    this._removeHash()
    this._getData()

    /* remove search-input value if active-filter-tag of search is reset */
    const searchEl = this.querySelector('artist-search')
    if (searchEl) {
      searchEl.searchTerm = e.detail
    }
  }

  private _handleFilterChange(e: any) {
    this._apiLink = e.detail
    this._removeHash()
    this._getData()
  }
  private _handleLoadMoreChange(e: any) {
    this._apiLink = e.detail
    this._removeHash()
    this._getData()
  }

  private _handleViewChange(e: any) {
    this._provider.setValue({view: e.detail});
    this.view = e.detail
  }

  private _removeHash () {
    if(window.history.pushState) {
      window.history.pushState('', '/', window.location.pathname)
    } else {
      window.location.hash = '';
    }
  }

  private _scrollToFilterList () {
    // don´t scroll down if user has not scrolled
    if (window.scrollY === 0) return
    const slot = this.shadowRoot?.querySelector('slot');
    const assignedElements = slot?.assignedElements({flatten: true})
    if(assignedElements) {
      const anchorSearch:any = assignedElements[0].querySelector('artist-search')
      const anchorList:any = assignedElements[0].querySelector('artist-list')
      let anchor = anchorSearch
      if (window.innerWidth >= 768) {
        anchor = anchorList
      }
      if (anchor) {
        let offset = 160
        let top = anchor.getBoundingClientRect().top - document.body.getBoundingClientRect().top - offset
        window.scrollTo({
          behavior: 'smooth',
          top: top
        })
      }
    }
  }

  private _reloadIfHashExistsAndFiltered() {
    let filterCount = this._provider.value.data?.searchRequest?.filter.length
    let searchTerm = this._provider.value.data?.searchRequest?.term
    const filtered = filterCount > 0
    const searched = searchTerm !== '' && searchTerm !== null
    if (filtered || searched) {
      location.reload()
    }
  }

  private _getApiLinkIdentifier() {
    const language = window.location.pathname.split('/')[1];
    const apiLinkIdentifier = language + '-apiLink';
    return apiLinkIdentifier;
  }

  private _getData () {
    this._getDataTask.run()
  }

  render() {
    return html`
        <slot></slot>
    `;
  }

  static styles = css`
      :host {
          display: block;
          position: relative;
          z-index: 1;
      }
  `;
}

declare global {
  interface HTMLElementTagNameMap {
    'artist-wrapper': ArtistWrapper
  }
}
